import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  Tab,
  Tabs,
  Grid,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Iconify from "src/components/iconify/Iconify";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import PurchasesTable from "src/components/purchases-table/PurchasesTable";
import { get } from "src/utils/networkUtils";
import MarketplaceStats from "src/components/marketplace/MarketplaceStats";
import TopPurchaseUsersTable from "src/components/purchases-table/TopPurchaseUsersTable";
import TopPurchaseProductsTable from "src/components/purchases-table/TopPurchaseProductsTable";
import useWithUrlTenant from "src/hooks/useTenant";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}
const getDefaultStartDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date;
};

export default function MarketplaceDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [purchases, setPurchases] = useState(null);
  const [valueInfo, setValueInfo] = useState(0);
  const [purchasesStats, setPurchasesStats] = useState(null);
  const [topUsersPurchases, setTopUsersPurchases] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const withTenant = useWithUrlTenant();
  
  // CSV download state
  const [downloadStartDate, setDownloadStartDate] = useState(getDefaultStartDate());
  const [downloadEndDate, setDownloadEndDate] = useState(getDefaultEndDate());

  // Check if we're on a child route
  const isChildRoute = location.pathname !== '/dashboard/marketplace';

  const handleChangeInfoTabs = (event, newValue) => {
    setValueInfo(newValue);
  };

  const downloadPurchasesCsv = () => {
    const params = new URLSearchParams();
    if (downloadStartDate) {
      params.append("startDate", downloadStartDate.toISOString());
    }
    if (downloadEndDate) {
      params.append("endDate", downloadEndDate.toISOString());
    }

    get(`/admin/marketplace/store-items/purchases/csv?${params.toString()}`, {
      responseType: "blob",
    }).then(function (response) {
      const downloadUrl = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "purchases.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  };

  useEffect(() => {
    // Only load dashboard data if we're on the main marketplace route
    if (!isChildRoute) {
      get(`/admin/marketplace/store-items/purchases`).then(function (
        response
      ) {
        setPurchases(response.purchases);
        setTopUsersPurchases(response.topUsers);
        setTopProducts(response.topProducts);
      });
      get(`/admin/marketplace/purchases/stats`).then(function (
        response
      ) {
        setPurchasesStats(response.stats);
      });
    }
  }, [isChildRoute]);
  return (
    <>
      <Helmet>
        <title> Store </title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Only show header when on main marketplace route */}
        {!isChildRoute && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" sx={{ mb: 5 }}>
              Store
            </Typography>

            <Grid
              container
              spacing={2}
              justifyContent={"flex-end"}
              sx={{ mb: 2 }}
            >
              <Grid
                item
                xs={8}
                md={2}
                lg={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  onClick={() =>
                    navigate(withTenant(`/dashboard/marketplace/store-items`))
                  }
                  variant="contained"
                  sx={{ mr: 1 }}
                  startIcon={
                    <Iconify
                      icon={"eva:settings-2-outline"}
                      width={24}
                      height={24}
                    />
                  }
                >
                  Coins
                </Button>
              </Grid>
              <Grid
                item
                xs={8}
                md={2}
                lg={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  onClick={() => navigate(withTenant(`/dashboard/marketplace/megaspins`))}
                  variant="contained"
                  startIcon={
                    <Iconify
                      icon={"eva:settings-2-outline"}
                      width={24}
                      height={24}
                    />
                  }
                >
                  Mega Spin
                </Button>
              </Grid>
            </Grid>
          </Stack>
        )}

        {/* Render child routes if we're on a child route, otherwise render dashboard content */}
        {isChildRoute ? (
          <Outlet />
        ) : (
          <>
            {purchasesStats !== null && <MarketplaceStats stats={purchasesStats} />}
            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={valueInfo}
                  onChange={handleChangeInfoTabs}
                  aria-label="basic tabs example"
                >
                  <Tab label="Purchases" {...a11yProps(0)} />
                  <Tab label="Top Purchasers" {...a11yProps(1)} />
                  <Tab label="Top Products" {...a11yProps(2)} />
                </Tabs>
              </Box>
              <TabPanel value={valueInfo} index={0}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={downloadStartDate}
                      onChange={(newValue) => setDownloadStartDate(newValue)}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                    <DatePicker
                      label="End Date"
                      value={downloadEndDate}
                      onChange={(newValue) => setDownloadEndDate(newValue)}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </LocalizationProvider>
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:download-outline" />}
                    onClick={downloadPurchasesCsv}
                  >
                    Download CSV
                  </Button>
                </Stack>
                {purchases !== null && <PurchasesTable purchases={purchases} />}
              </TabPanel>
              <TabPanel value={valueInfo} index={1}>
                <TopPurchaseUsersTable users={topUsersPurchases ?? []} />
              </TabPanel>
              <TabPanel value={valueInfo} index={2}>
                <TopPurchaseProductsTable users={topProducts ?? []} />
              </TabPanel>
            </Box>
          </>
        )}
      </Container>
    </>
  );
}
